// Fullscreen API is webkit-prefixed until Safari 16.4 — polyfill patches the unprefixed API
// eslint-disable-next-line simple-import-sort/imports
import 'fullscreen-polyfill';

import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';

import { ErrorBoundaryAppContainer } from '@rikstv/play-common/src/components/errorBoundary/ErrorBoundaryAppContainer';
import config from '@rikstv/play-common/src/config';
import { flagManager } from '@rikstv/play-common/src/utils/featureFlags/FeatureFlagManager';
import { initThirdPartyCookiesTester } from '@rikstv/play-common/src/utils/thirdPartyCookiesTester';
import { CastProvider } from '@rikstv/play-player/src/chromecast/CastProvider';
import { StrimProvider } from '@rikstv/shared-components';

import Router from './common/router/Router';
import store from './common/store/store';
import { bustOutOfActivationIframe } from './domain/activateExternalService/forces/externalService.utils';
import { checkForPreloadedVoucher } from './utils/voucher/voucher.utils';

import './index.css';

// If the app is loaded inside the external service activation iframe (inception!), this code will
// try to reroute the outer page to whatever page is requested inside the frame.
// This may happen for example if the activation form (on viaplay or similar) links to strim.no
bustOutOfActivationIframe();

checkForPreloadedVoucher();

initThirdPartyCookiesTester();

flagManager.init();

// Delete posthog cookie (set by accident due to careless configuration)
// can delete after 2025-03-01
document.cookie =
  'ph_phc_kqixdPiJcf3OETOVmnip0rBeBZz6CAZya64q7uN2ZIq_posthog=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

const rootElement = document.getElementById('root');
if (rootElement == null) {
  throw new Error('rootElement cannot be null');
}

const defaultOgImageUrl = `${config.baseApplicationUrl}/strimshare.webp`;

// use og:image from sanity frontPage document (updated at: https://cms.strim.no/strim/structure/forside;forside)
const ogImagePromise = fetch(
  'https://bj7ifs6s.apicdn.sanity.io/v2022-03-07/data/query/strim-prod?query=*%5B_type+%3D%3D+%22frontPage%22%5D+%7B%0A++%22img%22%3A+meta.image.asset-%3Eurl%0A%7D%5B0%5D'
)
  .then(res => {
    if (!res.ok) {
      throw new Error(`Failed to load og:image from Sanity: ${res.status}`);
    }

    return res.json();
  })
  .then(data => {
    const imageUrl = data?.result?.img;
    if (typeof imageUrl !== 'string' || imageUrl.length === 0) {
      throw new Error('Sanity og:image response is missing expected image URL');
    }

    const url = new URL(imageUrl);
    url.searchParams.set('w', '1200');
    url.searchParams.set('fit', 'max');
    url.searchParams.set('auto', 'format');

    return url.toString();
  })
  .catch(error => {
    console.warn('Could not load dynamic og:image, using fallback image instead', error);
    return defaultOgImageUrl;
  });

const App = () => {
  const [ogImageUrl, setOgImageUrl] = useState(defaultOgImageUrl);
  useEffect(() => {
    ogImagePromise.then(setOgImageUrl);
  }, []);
  return (
    <StrictMode>
      <ErrorBoundaryAppContainer>
        <HelmetProvider>
          <Helmet prioritizeSeoTags>
            <title>{config.basePageTitle}</title>
            <meta property="og:url" content={config.baseApplicationUrl} />
            <meta property="og:site_name" content={config.basePageTitle} />
            <meta property="og:image" content={ogImageUrl} />
            <meta property="al:android:app_name" content={config.androidApp.appName} />
            <meta property="al:android:package" content={config.androidApp.package} />
            <meta property="al:ios:app_name" content={config.iosApp.appName} />
            <meta property="al:ios:app_store_id" content={config.iosApp.appStoreId} />
          </Helmet>
          <StrimProvider>
            <Provider store={store}>
              <CastProvider>
                <Router />
              </CastProvider>
            </Provider>
          </StrimProvider>
        </HelmetProvider>
      </ErrorBoundaryAppContainer>
    </StrictMode>
  );
};

const root = createRoot(rootElement);
root.render(<App />);
